import React, { useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import cn from 'class-names';
import { useDispatch } from 'react-redux';

import { Button } from '@uikit/Form/Button/Button';
import { SVGIcon } from '@uikit/old/Icon/Icon';
import ResponaNewLogo from '@uikit/old/ResponaNewLogo/ResponaNewLogo';

import { changePublisherWebsiteStatusApi } from '@api/publishers.api';
import { PublisherWebsiteStatus } from 'respona_api/generated/common_pb';
import { addNotification } from '@redux/actions/notifications.actions';
import { linkGoTo, redirectUserTo } from '@utils/historyHandler';
import useCurrentWorkspaceId from '@hooks/useCurrentWorkspaceId';
import urls from '@constants/urls';

import './WebsiteStepLinks.scss';

const stepsLinksConf = (workspaceId: number, id: number, isDisabledStep: boolean) => [
  {
    key: 'addWebsite',
    title: 'Add Website',
    link: urls.WEBSITE_STEP_ONE(workspaceId, id),
    disabled: false,
  },
  {
    key: 'setYourPrice',
    title: 'Set your price',
    link: urls.WEBSITE_STEP_TWO(workspaceId, id),
    disabled: isDisabledStep,
  },
  {
    key: 'additionalDetails',
    title: 'Additional details',
    link: urls.WEBSITE_STEP_THREE(workspaceId, id),
    disabled: isDisabledStep,
  },
  {
    key: 'verifyOwnership',
    title: 'Verify ownership',
    link: urls.WEBSITE_STEP_FOUR(workspaceId, id),
    disabled: isDisabledStep,
  },
];

function WebsiteStepLinks({
  id,
  isDisabledStep,
  step,
  isVerified,
}: {
  id?: number;
  isDisabledStep: boolean;
  step: number;
  isVerified?: boolean;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>();
  const workspaceId = useCurrentWorkspaceId();
  const dispatch = useDispatch();
  const history = useHistory();
  const config = stepsLinksConf(workspaceId, id, isDisabledStep);
  const closeUrl =
    step === 1
      ? `/workspaces/${workspaceId}/websites`
      : `/workspaces/${workspaceId}/websites?refetch=true`;

  const onContinueClick = () => {
    changePublisherWebsiteStatusApi(id, workspaceId, PublisherWebsiteStatus.PW_ACTIVE)
      .then(() => {
        redirectUserTo(history, `${urls.WEBSITES(workspaceId)}?refetch=true`);
      })
      .catch((error) => {
        console.error(error);
        dispatch(addNotification({ title: 'Website status update failed', type: 'error' }));
      });
  };

  return (
    <div className="steps-links" ref={ref}>
      {/* RES-7362: equal side columns keep the steps centred whatever the right button says
          ("Close" vs "Add site without verification"), so they no longer jump on step 4. */}
      <div className="steps-links__side">
        <div className="steps-links-logo">
          <ResponaNewLogo size="small" />
        </div>
      </div>
      <div className="steps-links__steps">
        {config.map(({ link, key, title, disabled }, index, arr) => (
          <React.Fragment key={key}>
            <div
              className={cn('step-link__container', {
                'step-link__container--disabled': disabled,
              })}
            >
              <Link
                key={key}
                to={linkGoTo(link)}
                data-testid={`website-wizard-step-link-${index + 1}`}
                data-disabled={disabled ? 'true' : 'false'}
                className={cn('step-link', {
                  'step-link--active': index + 1 === step,
                })}
                onClick={(event) => {
                  event.preventDefault();

                  if (!disabled) {
                    redirectUserTo(history, link);
                  }
                }}
              >
                <div className="step-link__step">
                  {index + 1 < step ? <SVGIcon icon="check" color="#7B7B7B" size={6} /> : index + 1}
                </div>

                <div className="step-link__link-title">{title}</div>
              </Link>
            </div>
          </React.Fragment>
        ))}
      </div>
      <div className="steps-links__side steps-links__side--end">
        {step === 4 && !isVerified ? (
          <Button
            type="bordered-grey"
            onClick={() => onContinueClick()}
            className="steps-links__back-button-close"
            data-testid="website-wizard-add-without-verification"
            colorIcon="#7B7B7B"
            iconSize={9}
          >
            Add site without verification
          </Button>
        ) : (
          <Button
            type="bordered-grey"
            onClick={() => redirectUserTo(history, closeUrl)}
            className="steps-links__back-button-close"
            data-testid="website-wizard-close"
            leftIcon="close"
            colorIcon="#7B7B7B"
            iconSize={9}
          >
            Close
          </Button>
        )}
      </div>
    </div>
  );
}

export default WebsiteStepLinks;
